export function buildPdfBlob(drawContent, { image } = {}) {
  const objects = [];
  const addObject = body => {
    objects.push(body);
    return objects.length;
  };

  const fontId = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
  const boldFontId = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>');
  const imageId = image ? addObject(`<< /Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter [/ASCIIHexDecode /DCTDecode] /Length ${image.hex.length + 1} >>\nstream\n${image.hex}>\nendstream`) : null;
  const drawn = drawContent({ fontId, boldFontId });
  const pages = Array.isArray(drawn[0]) ? drawn : [drawn];
  const pageIds = pages.map(commands => {
    const content = commands.join('\n');
    const contentId = addObject(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
    return addObject(`<< /Type /Page /Parent 0 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 ${fontId} 0 R /F2 ${boldFontId} 0 R >> ${imageId ? `/XObject << /Logo ${imageId} 0 R >>` : ''} >> /Contents ${contentId} 0 R >>`);
  });
  const pagesId = addObject(`<< /Type /Pages /Kids [${pageIds.map(id => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`);
  pageIds.forEach(id => { objects[id - 1] = objects[id - 1].replace('/Parent 0 0 R', `/Parent ${pagesId} 0 R`); });
  const catalogId = addObject(`<< /Type /Catalog /Pages ${pagesId} 0 R >>`);

  const chunks = ['%PDF-1.4\n'];
  const offsets = [0];
  objects.forEach((body, index) => {
    offsets.push(chunks.join('').length);
    chunks.push(`${index + 1} 0 obj\n${body}\nendobj\n`);
  });
  const xrefOffset = chunks.join('').length;
  chunks.push(`xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`);
  offsets.slice(1).forEach(offset => chunks.push(`${String(offset).padStart(10, '0')} 00000 n \n`));
  chunks.push(`trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

  return new Blob(chunks, { type: 'application/pdf' });
}

export function createPdf(filename, drawContent, options) {
  const blob = buildPdfBlob(drawContent, options);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export function pdfEscape(value) {
  return String(value ?? '')
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');
}

export function estimatePdfTextWidth(text, size, bold = false) {
  return String(text ?? '').length * size * (bold ? 0.56 : 0.52);
}

export function pdfText(text, x, y, options = {}) {
  const { size = 10, bold = false, align = 'left', color = '0 0 0' } = options;
  const width = estimatePdfTextWidth(text, size, bold);
  const tx = align === 'center' ? x - width / 2 : align === 'right' ? x - width : x;
  return [
    'BT',
    `${color} rg`,
    `/${bold ? 'F2' : 'F1'} ${size} Tf`,
    `1 0 0 1 ${Number(tx).toFixed(2)} ${Number(y).toFixed(2)} Tm`,
    `(${pdfEscape(text)}) Tj`,
    'ET',
  ].join('\n');
}

export function wrapPdfText(text, maxWidth, size = 10, bold = false) {
  const words = String(text ?? '').split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';

  words.forEach(word => {
    const candidate = line ? `${line} ${word}` : word;
    if (estimatePdfTextWidth(candidate, size, bold) <= maxWidth) {
      line = candidate;
      return;
    }
    if (line) lines.push(line);
    line = word;
  });

  if (line) lines.push(line);
  return lines;
}

export function pdfLine(x1, y1, x2, y2, color = '0.85 0.87 0.91', width = 1) {
  return `${color} RG\n${width} w\n${x1} ${y1} m\n${x2} ${y2} l\nS`;
}

export function pdfRect(x, y, width, height, options = {}) {
  const { stroke = '0.85 0.87 0.91', fill = null, lineWidth = 1 } = options;
  const commands = [];
  if (fill) commands.push(`${fill} rg`);
  commands.push(`${stroke} RG`, `${lineWidth} w`, `${x} ${y} ${width} ${height} re`, fill ? 'B' : 'S');
  return commands.join('\n');
}
